import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, Button } from '@t-one-internal/design-system';
import { getReportDownloadUrl, getPublicDownloadUrl } from '../api/downloadApi';

type DownloadStatus = 'loading' | 'success' | 'error';

interface DownloadPageProps {
  type: 'public' | 'authenticated';
}

const DownloadPage = ({ type }: DownloadPageProps) => {
  const { token, id } = useParams<{ token?: string; id?: string }>();
  const [status, setStatus] = useState<DownloadStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const [filename, setFilename] = useState<string>('Report');

  const triggerDownload = async () => {
    setStatus('loading');
    setError(null);

    try {
      let response;

      if (type === 'public' && token) {
        response = await getPublicDownloadUrl(token);
      } else if (type === 'authenticated' && id) {
        response = await getReportDownloadUrl(Number(id));
      } else {
        throw new Error('Invalid download parameters');
      }

      if (!response.success || !response.download_url) {
        throw new Error(response.error || 'Failed to get download URL');
      }

      if (response.filename) {
        setFilename(response.filename);
      }

      // Redirect to the presigned URL to trigger download
      window.location.href = response.download_url;
      setStatus('success');
    } catch (err: any) {
      setStatus('error');
      setError(err.message || 'Download failed');
    }
  };

  useEffect(() => {
    // Small delay to allow the page to render before triggering download
    const timer = setTimeout(() => {
      triggerDownload();
    }, 500);

    return () => clearTimeout(timer);
  }, [token, id, type]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-8 text-center">
          {status === 'loading' && (
            <>
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-6"></div>
              <h1 className="text-xl font-semibold text-foreground mb-2">
                Preparing Your Download
              </h1>
              <p className="text-muted-foreground">
                Please wait while we prepare your file...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h1 className="text-xl font-semibold text-foreground mb-2">
                Download Started
              </h1>
              <p className="text-muted-foreground mb-4">
                Your download of <strong>{filename}</strong> should start automatically.
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                If your download doesn't start, click the button below.
              </p>
              <Button onClick={triggerDownload} variant="outline">
                Download Again
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-8 h-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h1 className="text-xl font-semibold text-foreground mb-2">
                Download Failed
              </h1>
              <p className="text-red-600 mb-6">
                {error || 'An error occurred while preparing your download.'}
              </p>
              <Button onClick={triggerDownload}>
                Try Again
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DownloadPage;
